/** Lossless SVG and high-DPI PNG export, derived from the retained model. */
import type { Scene } from '../core/scene';
import { cssColor } from '../core/scene';
import { pathToSvgD } from '../core/geometry';
import type { SceneRenderer } from '../render/renderer';

export function exportSvg(scene: Scene): string {
  const defs: string[] = [];
  const body: string[] = [];
  scene.shapes.forEach((s, i) => {
    const d = pathToSvgD(s.path);
    let paint = cssColor(s.style.color);
    if (s.gradient && s.gradient.stops.length > 0) {
      const id = `alc-grad-${i}`;
      const stops = s.gradient.stops
        .map((st) => `<stop offset="${Math.max(0, Math.min(1, st.offset))}" stop-color="${cssColor(st.color)}"/>`)
        .join('');
      defs.push(
        `<linearGradient id="${id}" gradientUnits="userSpaceOnUse" x1="${s.gradient.from.x}" y1="${s.gradient.from.y}" x2="${s.gradient.to.x}" y2="${s.gradient.to.y}">${stops}</linearGradient>`,
      );
      paint = `url(#${id})`;
    }
    if (s.style.mode === 'fill') {
      const rule = s.style.fillRule === 'evenodd' ? ' fill-rule="evenodd"' : '';
      body.push(`<path d="${d}" fill="${paint}"${rule}/>`);
    } else {
      body.push(
        `<path d="${d}" fill="none" stroke="${paint}" stroke-width="${s.style.lineWidth}" stroke-linejoin="${s.style.join}" stroke-linecap="${s.style.cap}"/>`,
      );
    }
  });
  const parts: string[] = [];
  parts.push(
    `<svg xmlns="http://www.w3.org/2000/svg" width="${scene.width}" height="${scene.height}" viewBox="0 0 ${scene.width} ${scene.height}">`,
  );
  if (defs.length) parts.push(`<defs>${defs.join('')}</defs>`);
  parts.push(`<rect width="100%" height="100%" fill="${cssColor(scene.background)}"/>`);
  parts.push(...body);
  parts.push('</svg>');
  return parts.join('\n');
}

function download(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function stamp(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
}

export function exportSvgFile(scene: Scene): void {
  download(`alchemy-${stamp()}.svg`, new Blob([exportSvg(scene)], { type: 'image/svg+xml' }));
}

export function exportPngFile(renderer: SceneRenderer, scene: Scene, scale = 2): void {
  const canvas = renderer.renderSceneToCanvas(scene, scale);
  canvas.toBlob((blob) => {
    if (blob) download(`alchemy-${stamp()}.png`, blob);
  }, 'image/png');
}
